'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { Lightbulb } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface OfferSectionProps {
  translationNamespace: string;
  paragraphKeys?: string[];
}

export function OfferSection({
  translationNamespace,
  paragraphKeys = ['paragraph1', 'paragraph2']
}: OfferSectionProps) {
  const t = useTranslations(translationNamespace);
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  return (
    <SectionWrapper ref={sectionRef.ref} variant="gradient" padding="md" className="relative">
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
        <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
      </div>

      <div className="container relative px-4 z-10">
        {/* Title Section */}
        <div className={cn(
          "mb-12 transition-all duration-700",
          sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        )}>
          <SectionHeading
            page=""
            fallbackTitle={t('title')}
            align="left"
          />
        </div>

        {/* Content Section */}
        <div className="space-y-8">
          {paragraphKeys.map((key, index) => {
            const text = t(key);
            if (!text || text === key) return null;

            const isFirst = index === 0;
            const iconColor = isFirst ? 'from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700' : 'from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700';
            const borderColor = isFirst ? 'border-primary-500 hover:border-primary-600' : 'border-blue-500 hover:border-blue-600';
            const accentColor = isFirst ? 'from-primary-500/10' : 'from-blue-500/10';
            const patternColor = isFirst ? 'bg-primary-500' : 'bg-blue-500';

            return (
              <div
                key={key}
                className={cn(
                  "relative group transition-all duration-700",
                  sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                )}
                style={{ transitionDelay: `${200 + index * 200}ms` }}
              >
                {/* Icon - Always visible with enhanced styling */}
                <div className={cn(
                  "absolute -top-3 -left-3 w-14 h-14 rounded-full bg-gradient-to-br flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 z-10",
                  iconColor
                )}>
                  <Lightbulb className="w-7 h-7 text-white" fill="currentColor" />
                </div>

                {/* Decorative corner accent */}
                <div className={cn(
                  "absolute top-0 left-0 w-20 h-20 bg-gradient-to-br to-transparent rounded-tl-xl",
                  accentColor
                )}></div>

                <div className={cn(
                  "relative bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-10 pl-12 md:pl-14 border-l-4 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group-hover:-translate-y-1",
                  borderColor
                )}>
                  {/* Subtle background pattern */}
                  <div className="absolute inset-0 opacity-5 dark:opacity-10">
                    <div className={cn(
                      "absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl",
                      patternColor
                    )}></div>
                  </div>

                  <div className="relative z-10">
                    <Typography variant="p">
                      {text}
                    </Typography>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SectionWrapper>
  );
}
